<script setup lang="ts">
import { from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computed, onMounted, ref } from 'vue';

import { fromNutsJson } from '@/api';
import { browseProducts } from '@/api/algolia';
import {
  changeOrderPaymentMethodAndAuthorize,
  ChangeOrderPaymentMethodResponse,
} from '@/api/order';
import type { TokenizedPaymentMethod } from '@/api/paymentMethod';
import EditBillingInfoModal from '@/components/account/auto-delivery/details/EditBillingInfoModal.vue';
import AddressBuilder from '@/components/account/order-details/AddressBuilder.vue';
import FormAddOrder from '@/components/account/order-details/FormAddOrder.vue';
import LineItem from '@/components/account/order-details/LineItem.vue';
import PaymentInfo from '@/components/account/order-details/PaymentInfo.vue';
import ShipmentInfo from '@/components/account/order-details/ShipmentInfo.vue';
import InformationBanner from '@/components/base/layout/InformationBanner.vue';
import RouteLink from '@/components/base/RouteLink.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import LeaveAReviewModal from '@/components/reviews/leave-a-review/LeaveAReviewModal.vue';
import { useDataPromo } from '@/composables/analytics/useDataPromo';
import { useCallback } from '@/composables/useCallback';
import money from '@/filters/money';
import { SkuForReview } from '@/lib/reviews';
import { useCustomer } from '@/stores/customer';
import { gtag } from '@/utils/analytics';
import { isDefined } from '@/utils/isDefined';
import { Money } from '@/utils/money';
import type { LineItemsOrderDetails, OrderDetailsPayload } from '@/utils/orderDetails';
import { ProductCard, ProductCardData } from '@/utils/productCard';
import { reportError } from '@/utils/reportError';

const props = defineProps<{
  isLoggedIn: boolean;
  order: OrderDetailsPayload;
  origin?: string;
}>();

useDataPromo();

const banner = ref<{ message: string; variant: 'success' | 'error' } | null>(null);
const currentOrder = ref<OrderDetailsPayload>(props.order);
const isBillingInfoModalOpen = ref(false);
const productData = ref<Map<string, ProductCardData>>(new Map());

const skuForReview = ref<SkuForReview>();

const sameAddress = computed(
  () =>
    currentOrder.value.shipments.filter(
      (shipment) =>
        currentOrder.value.bill_to.postal_code === shipment.shipment_info.address.postal_code,
    ).length,
);
const showReview = computed(() => !!currentOrder.value.review_token);

const showBanner = (message: string, variant: 'success' | 'error' = 'success') => {
  banner.value = { message, variant };
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const openEditBillingModal = () => {
  isBillingInfoModalOpen.value = true;
};

const updatePaymentMethod = useCallback(
  async (paymentMethod: TokenizedPaymentMethod, done: () => void) => {
    const { customer } = useCustomer();
    if (!customer) return;
    let response;
    try {
      response = await fromNutsJson<ChangeOrderPaymentMethodResponse>(
        changeOrderPaymentMethodAndAuthorize({
          orderId: currentOrder.value.order_id,
          customerId: customer.id,
          paymentMethod: {
            ...paymentMethod,
          },
        }),
      );
      currentOrder.value = response.order;
    } finally {
      if (!response || response.status === 'error') {
        showBanner('Payment Declined. Please update payment information.', 'error');
      } else {
        showBanner('Payment Info updated. Your payment is now processing.');
      }
      done();
      isBillingInfoModalOpen.value = false;
    }
  },
);

const fromDisplayPrice = (displayPrice: string): Money =>
  from(Number(displayPrice.replace(/[^0-9.]+/g, '')));

const formattedShipmentsItems = computed(() => {
  const { shipments } = currentOrder.value;

  return shipments.map((shipment) => {
    const parentItems: LineItemsOrderDetails[] = shipment.line_items.filter(
      (item) => !item.parent_id,
    );

    const formattedLineItems = parentItems.map((item) => {
      const children = shipment.line_items.filter((i) => i.parent_id === item.id);
      const unitPrice = Money.sum([
        fromDisplayPrice(item.unit_price),
        ...children.map((child) => fromDisplayPrice(child.unit_price)),
      ]);
      const totalPrice = Money.sum([
        fromDisplayPrice(item.total),
        ...children.map((child) => fromDisplayPrice(child.total)),
      ]);
      return { ...item, unit_price: money(unitPrice), total: money(totalPrice) };
    });

    return formattedLineItems;
  });
});

const skuForReviewProxy = computed<SkuForReview | undefined>({
  get: () => skuForReview.value,
  set: (value?: SkuForReview) => {
    if (typeof value?.review?.rating === 'number') {
      const sku = skuForReview.value;
      if (!sku) return;

      const shipments = currentOrder.value.shipments.map((shipment) => {
        const lineItems = shipment.line_items.map(
          (item): LineItemsOrderDetails =>
            item.id === sku.orderSku.id ? { ...item, review: value.review } : item,
        );
        return { ...shipment, line_items: lineItems };
      });

      currentOrder.value = { ...currentOrder.value, shipments };
    }

    skuForReview.value = value;
  },
});

const prepareDataForReview = (item: LineItemsOrderDetails): SkuForReview | undefined => {
  if (!item) return undefined;
  const { id, name, quantity, packaging, url, review } = item;
  return {
    image: item.image_url,
    orderNumber: currentOrder.value.external_id,
    orderSku: {
      id,
      itemName: name,
      quantity,
      skuName: packaging,
    },
    review,
    reviewStatus: 'reviewAllowed',
    url,
    token: currentOrder.value.review_token || '',
  };
};

onMounted(async () => {
  if (
    props.origin &&
    props.origin === 'email' &&
    currentOrder.value.status_description === 'Authorization Declined' &&
    currentOrder.value.status_message &&
    props.order.auto_delivery
  ) {
    banner.value = {
      message: 'Payment Declined. Please update payment information.',
      variant: 'error',
    };
    isBillingInfoModalOpen.value = true;
  }
  gtag('set', 'content_group', 'Account');

  const skus = currentOrder.value.shipments
    .flatMap((shipment) => shipment.line_items)
    .map(({ sku }) => sku)
    .filter(isDefined);
  if (skus.length) {
    try {
      const algoliaVariants = await browseProducts({
        facetFilters: [skus.map((sku) => `sku:${sku}`)],
      });
      productData.value = algoliaVariants.reduce((acc, variant) => {
        acc.set(variant.sku, ProductCard.fromAlgolia(variant));
        return acc;
      }, new Map<string, ProductCardData>());
    } catch (error) {
      reportError(error, 'failed to get Algolia results');
      productData.value = new Map();
    }
  }
});
</script>

<template>
  <div class="order-details">
    <InformationBanner v-if="banner" class="mb-4" :variant="banner.variant">
      <BaseBodyText v-if="banner.variant === 'success'">
        {{ banner.message }}
      </BaseBodyText>
      <BaseBodyText v-if="banner.variant === 'error'">
        {{ banner.message }}
        <a
          class="p-0 mt-3 ml-1 bg-transparent border-0 appearance-none cursor-pointer w-min"
          type="button"
          @click="openEditBillingModal"
        >
          <span class="mt-2 text-black underline font-proxima-nova">Update Payment</span>
        </a>
      </BaseBodyText>
    </InformationBanner>
    <div v-if="isLoggedIn">
      <span class="prev">&nbsp;</span>
      <RouteLink to="/account/order-history"> Return to Order History</RouteLink>
    </div>
    <h2 class="px-3 mt-5">Order number {{ currentOrder.external_id }}</h2>
    <p v-if="currentOrder.purchase_order_number" class="px-3 mt-5">
      <strong>PO Number {{ currentOrder.purchase_order_number }}</strong>
    </p>
    <div class="mt-2.5 px-3">
      Want to order these items again?
      <FormAddOrder :activeItems="currentOrder.activeItems" :id="currentOrder.order_id" />
    </div>
    <div v-if="currentOrder?.military_message" class="mt-2.5">
      {{ currentOrder.military_message }}
    </div>

    <template
      v-for="(shipment, index) in currentOrder.shipments"
      :key="shipment.shipment_info.address.postal_code"
    >
      <div class="flex flex-wrap justify-between mb-5">
        <div class="w-full px-3 mt-4 sm:w-1/3">
          <h3 class="text-lg">Shipping Address:</h3>
          <AddressBuilder :address="shipment.shipment_info.address" />
        </div>
        <div v-if="currentOrder.showPricing" class="w-full px-3 mt-4 sm:w-1/3">
          <PaymentInfo
            :paymentInfo="currentOrder.payment_info"
            :order="currentOrder"
            @edit-billing="openEditBillingModal"
          />
          <p v-if="currentOrder.payment_info.type" class="escape-legacy">
            <b>Billing Address:</b><br />
          </p>
          <AddressBuilder v-if="!sameAddress" :address="currentOrder.bill_to" />
          <p v-else class="escape-legacy">Same as shipping<br /></p>
        </div>
        <div class="w-full px-3 mt-4 sm:w-1/3">
          <ShipmentInfo
            :orderExternalId="currentOrder.external_id"
            :shipmentInfo="shipment.shipment_info"
          />
        </div>
      </div>

      <div class="hidden w-full lg:flex lg:visible gap-x-6 mb-1.5">
        <BaseBodyText class="w-24 font-bold">Photo</BaseBodyText>
        <BaseBodyText class="flex-1 font-bold">Product details</BaseBodyText>
        <BaseBodyText v-if="currentOrder.showPricing" class="font-bold text-right">
          Total cost
        </BaseBodyText>
        <BaseBodyText class="font-bold text-right" :class="showReview ? 'w-64' : 'w-32'">
          Add to cart
        </BaseBodyText>
      </div>
      <div
        class="border-solid border-y border-nuts-neutral-200"
        data-test="order-details-line-items"
      >
        <ul class="flex flex-col w-full divide-y divide-solid divide-nuts-neutral-200">
          <LineItem
            v-for="(item, i) in formattedShipmentsItems[index]"
            :key="item.url"
            :lineItem="item"
            :productData="productData.get(item.sku)"
            :showPricing="currentOrder.showPricing"
            :showReviewButton="showReview"
            :showReviewPopper="i === 0"
            @review="(item) => (skuForReview = prepareDataForReview(item))"
          />
        </ul>
      </div>
    </template>
    <div class="flex justify-end mt-4">
      <div v-if="currentOrder.showPricing">
        <template v-for="total in currentOrder.totals" :key="total.label">
          <div
            class="grid grid-cols-2 mt-1"
            :class="{
              'border-t border-nuts-neutral-200 border-solid pt-1': total.label === 'Order total',
            }"
          >
            <div
              class="text-end"
              :class="{
                'font-bold': total.label === 'Subtotal' || total.label === 'Order total',
              }"
            >
              {{ total.label }}:
            </div>
            <div
              class="text-end"
              :class="{
                'font-bold': total.label === 'Subtotal' || total.label === 'Order total',
              }"
            >
              {{ total.amount }}
            </div>
          </div>
        </template>
      </div>
    </div>
    <EditBillingInfoModal
      allowSameMethod
      canSetAsDefault
      declined
      :isLoading="updatePaymentMethod.isPending"
      :isOpen="isBillingInfoModalOpen"
      :failedPaymentMethod="currentOrder.payment_info"
      @handle-close="isBillingInfoModalOpen = false"
      @handle-update="updatePaymentMethod.execute"
    />
    <LeaveAReviewModal
      v-if="showReview && skuForReviewProxy"
      v-model="skuForReviewProxy"
      isOpen
      page="order-details"
      @handle-close="skuForReview = undefined"
    />
  </div>
</template>

<style lang="scss" scoped>
.prev {
  display: inline-block;
  padding-right: 7px;
  font-size: $font-size-tiny;

  &:after {
    content: ' ';
    display: inline-block;
    margin-left: 8px;
    @include retina-sprite($sprite-caret-left-1x);
  }
}
</style>
